/**
 * Username validation and login-identifier parsing.
 *
 * Every account has a unique username chosen at signup (SPEC.md "Identity:
 * usernames first, email optional"). Rules mirror validateSlug in shape but use
 * the username character set, the reserved-username list, and the shared
 * profanity filter. Login accepts a username OR an email in one field, so this
 * module also classifies which one an identifier is.
 */
import { RESERVED_USERNAMES, USERNAME_CHARSET, USERNAME_MAX_LENGTH, USERNAME_MIN_LENGTH, } from './constants.js';
import { filterReason } from './profanity.js';
/**
 * Validate a username. Rules, checked in order:
 *  1. length between 3 and 20
 *  2. only letters, digits, and dashes
 *  3. no leading or trailing dash
 *  4. not a reserved name (case-insensitive)
 *  5. no profanity
 */
export function validateUsername(username) {
    if (username.length < USERNAME_MIN_LENGTH)
        return { valid: false, reason: 'too_short' };
    if (username.length > USERNAME_MAX_LENGTH)
        return { valid: false, reason: 'too_long' };
    if (!USERNAME_CHARSET.test(username))
        return { valid: false, reason: 'invalid_chars' };
    if (username.startsWith('-') || username.endsWith('-')) {
        return { valid: false, reason: 'edge_dash' };
    }
    if (RESERVED_USERNAMES.includes(username.toLowerCase())) {
        return { valid: false, reason: 'reserved' };
    }
    const bad = filterReason(username.replace(/-/g, ' '));
    if (bad)
        return { valid: false, reason: 'profanity', detail: bad };
    return { valid: true };
}
/** Convenience boolean wrapper around validateUsername. */
export function isValidUsername(username) {
    return validateUsername(username).valid;
}
/** Kid-friendly copy for each rejection reason. Plain words, no em dashes. */
export const USERNAME_REJECT_MESSAGES = {
    too_short: `That name is a little short. Use at least ${USERNAME_MIN_LENGTH} characters.`,
    too_long: `That name is a bit long. Keep it to ${USERNAME_MAX_LENGTH} characters or less.`,
    invalid_chars: 'Usernames can use letters, numbers, and dashes only.',
    edge_dash: 'Usernames cannot start or end with a dash.',
    reserved: 'That name is saved for the app. Please pick another one.',
    profanity: "Let's pick a friendlier name.",
};
/** Friendly message for a validation result, or null when the name is fine. */
export function usernameRejectMessage(result) {
    return result.valid ? null : USERNAME_REJECT_MESSAGES[result.reason];
}
/**
 * Classify a login identifier as an email or a username. An email always
 * contains "@"; a username never can (it is outside the username character
 * set), so the presence of "@" is a clean, unambiguous discriminator. The
 * caller looks the account up by email or by username accordingly, and email
 * login only succeeds when that email is actually on file for the account.
 */
export function classifyLoginIdentifier(identifier) {
    return identifier.includes('@') ? 'email' : 'username';
}
//# sourceMappingURL=username.js.map